import { AssetType } from 'config/consts';
import React, { useState, useEffect, useRef } from 'react';
import { merge } from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import Highcharts from 'highcharts';
import svgToDataURL from 'svg-to-dataurl';
import {
  PortfolioRatioChartDatum,
  PortfolioRatioChartProps,
  TypeKey,
} from './PortfolioRatioStuffing';
import {
  ChartForPreviewInternalProps,
  deconstructForPreviewSetter,
} from 'components/features/preview-share/useChartRenderer';
import { commaFormat } from 'components/features/new-report/utils/formatter';
import { marker } from 'components/features/utils/chartUtils';

const getColor = (type: TypeKey): string => {
  switch (type) {
    case AssetType['国内現物']:
      return '#179a8d';
    case AssetType['信用']:
      return '#7ad8c6';
    case AssetType['ロボアドバイザー']:
      return '#f47f37';
    case AssetType['投資信託']:
      return '#dd80d4';
    case AssetType['預金_MRF']:
      return '#9f3bf4';
    case AssetType['その他']:
      return '#d2deef';
    case AssetType['外国株式']:
      return '#3fa9f5';
    case AssetType['未上場株式']:
      return '#8C8C8C';
    case AssetType['国内ETF・ETN']:
      return '#6D71DA';
    case AssetType['外国ETF・ETN']:
      return '#17CECE';
    case AssetType['国内REIT']:
      return '#FE5F5F';
    case AssetType['外国REIT']:
      return '#D09494';
    case AssetType['国内債券']:
      return '#26E016';
    case AssetType['外国債券']:
      return '#F0C22B';
    case AssetType['国内EB']:
      return '#4098B4';
    case AssetType['外国EB']:
      return '#91BEAB';
    case AssetType['不動産']:
      return '#FF58DA';
    case 'JP_EQ':
      return '#49B9A9';
    case 'DC_EQ':
      return '#2563EB';
    case 'EM_EQ':
      return '#E77E7E';
    case 'JP_FI':
      return '#A3E635';
    case 'DC_FI':
      return '#53AA34';
    case 'EM_FI':
      return '#B22F13';
    case 'JP_REIT':
      return '#FE5F5F';
    case 'G_REIT':
      return '#A37070';
    case 'OTHER':
      return '#D2DEEF';
    case 'General':
      return '#0284C7';
    case 'Identification':
      return '#64748B';
    case 'Nisa':
      return '#EB5E5E';
    case 'DefinedContributionPension':
      return '#349C03';
    case 'Other':
      return '#D2DEEF';
  }
  return '';
};

export const getTypeKeyToString = (
  type: TypeKey,
  isRoboShortLabel = false
): string => {
  switch (type) {
    case AssetType['国内現物']:
      return '国内株式';
    case AssetType['信用']:
      return '信用取引';
    case AssetType['投資信託']:
      return '投資信託';
    case AssetType['ロボアドバイザー']:
      return isRoboShortLabel ? 'ロボアド' : 'ロボアドバイザー';
    case AssetType['預金_MRF']:
      return '預金・MRF';
    case AssetType['その他']:
      return 'その他';
    case AssetType['外国株式']:
      return '外国株式';
    case AssetType['未上場株式']:
      return '未上場株式';
    case AssetType['国内ETF・ETN']:
      return '国内ETF・ETN';
    case AssetType['外国ETF・ETN']:
      return '外国ETF・ETN';
    case AssetType['国内REIT']:
      return '国内REIT';
    case AssetType['外国REIT']:
      return '外国REIT';
    case AssetType['国内債券']:
      return '国内債券';
    case AssetType['外国債券']:
      return '外国債券';
    case AssetType['国内EB']:
      return '国内EB';
    case AssetType['外国EB']:
      return '外国EB';
    case AssetType['不動産']:
      return '不動産';
    case 'General':
      return '一般';
    case 'Identification':
      return '特定口座';
    case 'Nisa':
      return 'NISA';
    case 'DefinedContributionPension':
      return '確定拠出年金';
    case 'Other':
      return 'その他';
    case 'JP_EQ':
      return '国内株式';
    case 'DC_EQ':
      return '先進国株式';
    case 'EM_EQ':
      return '新興国株式';
    case 'JP_FI':
      return '国内債券';
    case 'DC_FI':
      return '先進国債券';
    case 'EM_FI':
      return '新興国債券';
    case 'JP_REIT':
      return '国内REIT';
    case 'G_REIT':
      return '外国REIT';
    case 'OTHER':
      return 'その他';
  }
  return '';
};

const dataToSeries = (
  data: PortfolioRatioChartDatum[]
): Highcharts.SeriesOptionsType[] => {
  return [
    {
      type: 'pie',
      data: data.map(
        (datum): Highcharts.PointOptionsObject => {
          const color = getColor(datum.key);
          const label = getTypeKeyToString(datum.key, true);
          return {
            color,
            name: label,
            title: label,
            y: datum.ratio,
          };
        }
      ),
      showInLegend: true,
      dataLabels: {
        enabled: true,
        distance: '-30%',
        formatter: function(): string {
          const num = this.point.y as number;
          return `${commaFormat(num, { numberOfDecimals: 1 })}%`;
        },
        style: {
          color: 'white',
          textOutline: 'none',
          fontWeight: 'bold',
        },
      },
    },
  ];
};

export const PortfolioRatioChartInternal = ({
  data,
  options: options_,
  forPreviewSetter,
}: PortfolioRatioChartProps &
  ChartForPreviewInternalProps): React.ReactElement => {
  const { setChartDataURL } = deconstructForPreviewSetter(forPreviewSetter);
  const series = dataToSeries(data.filter(datum => datum.ratio >= 0));
  const [options, setOptions] = useState<Highcharts.Options>({
    exporting: { enabled: false },
    credits: { enabled: false },
    title: {
      text: '',
    },
    subtitle: {
      text: '',
    },
    legend: {
      enabled: true,
      align: 'right',
      verticalAlign: 'middle',
      layout: 'vertical',
      itemMarginTop: 10,
      maxHeight: 195,
      itemStyle: {
        fontWeight: 'normal',
        fontSize: '10px',
        color: '#ffffff',
      },
      navigation: {
        activeColor: '#fdba74',
        arrowSize: 12,
        style: { color: '#ffffff', x: 20 },
      },
    },
    chart: {
      backgroundColor: '#475569',
      height: 255,
    },
    tooltip: {
      outside: true,
      borderColor: 'transparent',
      pointFormatter: function(): string {
        return `${commaFormat(this.y ?? 0, { numberOfDecimals: 1 })}%`;
      },
    },
    plotOptions: {
      series: {
        ...marker,
        borderColor: '#475569',
        borderWidth: 2,
        turboThreshold: 15000,
      },
      pie: {
        innerSize: '50%',
      },
    },
    series,
  });

  useEffect(() => {
    setOptions(prev => {
      return (options_
        ? (x: Highcharts.Options) => merge(x, options_)
        : (x: Highcharts.Options) => x)({
        ...prev,
        series,
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, options_]);

  useEffect(() => {
    setChartDataURL &&
      ref.current &&
      setChartDataURL(svgToDataURL(ref.current.chart.getSVG()));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options]);
  const ref = useRef<HighchartsReact.RefObject>(null);

  return (
    <>
      <HighchartsReact highcharts={Highcharts} options={options} ref={ref} />
    </>
  );
};
